/**
 * @file BuilderCanvas.tsx
 * @description Канва конструктора — настоящая витрина в iframe, одна на все
 * панели (phase19.16 Р1-6а; uiux-builder-shell.md §2.1; контракт §14.1).
 *
 * Черновик: вход ключом вида сайта с путём страницы (`/site-preview?token=…&path=
 * /preview/<slug>`) — шапка, подвал и цвета черновые. «Сейчас на сайте» —
 * опубликованный адрес. «Посмотреть» дизайн или версию — тем же входом с источником
 * `design` / `revision` и полосой над канвой; версия страницы (Р3а) —
 * `pageRevisionFrameUrl`, подпись версии рисует витрина, у нас — только кнопки.
 * Связь с витриной: живое оформление (перерисовка — только когда сохранено
 * то, что витрина собирает на сервере, `canvasReloadKey`) и прокрутка к шапке
 * или подвалу (`useScaledFrame`), клики по блокам и элементам, «+ блок сюда»
 * (`useCanvasBridge`), по шапке, подвалу и корзине — выбирают их справа.
 * Наложения поверх сайта — корзина с образцом (SB120 этап 4) и баннер cookie
 * (Р3г) — `useCanvasOverlays`.
 */

import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Skeleton } from "../../ui/skeleton";
import { useScaledFrame } from "../../site-view/preview/useScaledFrame";
import { useCanvasFontRegistry } from "../../site-view/shop-fonts-context";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { CanvasCartNotice } from "./CanvasCartNotice";
import { CanvasLoading } from "./CanvasLoading";
import { CanvasTargetBar } from "./CanvasTargetBar";
import { CanvasVersionBar } from "./CanvasVersionBar";
import { CanvasToolbar, type CanvasScale } from "./CanvasToolbar";
import { useCanvasBridge } from "../../../hooks/site-builder/useCanvasBridge";
import { useCanvasOverlays } from "../../../hooks/site-builder/useCanvasOverlays";
import { useCanvasReloadTick } from "../../../hooks/site-builder/useCanvasReloadTick";
import { useSitePreviewToken } from "../../../hooks/site-view/useSitePreviewToken";
import { canvasPreviewLoaded, canvasPreviewUnloaded } from "../../../hooks/site-builder/useCanvasPreview";
import { canvasPath, canvasReloadKey, deviceForCanvas } from "../../../lib/site-builder/canvas";
import { draftFrameUrl, liveFrameUrl, pageRevisionFrameUrl } from "../../../lib/site-view/preview";
import { cn } from "@/lib/utils";

const UNAVAILABLE = "Предпросмотр сейчас недоступен. Настройки сохраняются — проверить вид можно после публикации.";

interface Props {
  className?: string;
  /** Панель ширины, режима и масштаба (на телефоне под шторкой — без неё). */
  toolbar?: boolean;
}

export function BuilderCanvas({ className, toolbar = true }: Props) {
  const view = useSiteView();
  const b = useBuilder();
  const { selection, pages, override } = b;
  const state = view.preview;
  const [scale, setScale] = useState<CanvasScale>("fit");
  const mode = override ? "draft" : state.mode;
  const page = pages.page;
  const { ensure, products } = state.targets;

  // Товар для примера: выбранный, пример шаблона (Р2а) или первый товар в каталоге.
  const sampleSlug = b.canvasTarget.product?.slug ?? pages.template?.sample?.slug ?? null;
  useEffect(() => void (selection.screen === "product" && !sampleSlug && ensure("product")), [ensure, selection.screen, sampleSlug]);
  // Дизайн или версия — по опубликованному адресу: ключ снимка /preview/<slug> не открывает.
  const pageVersion = override?.pageVersion ?? null; // Р3а: вход витрины с pageId и version.
  const path = pageVersion ? "/" : canvasPath(selection, page, override ? "snapshot" : mode, sampleSlug ?? products?.[0]?.slug ?? null, b.canvasTarget.category);
  const token = useSitePreviewToken(mode === "draft" ? override?.source ?? { source: "draft" } : null, path);
  const area = selection.sel.kind === "area" ? selection.sel.area : null;
  const frame = useScaledFrame(state.width, {
    theme: override ? undefined : view.parts?.theme.theme,
    fontRegistry: useCanvasFontRegistry(),
    area,
    onAreaSelect: override ? undefined : (picked) => void b.select({ sel: { kind: "area", area: picked }, panel: null }),
    onCartSelect: override ? undefined : () => void b.select({ panel: "cart", sel: { kind: "page" } }),
    fit: scale === "fit",
    iframeRef: b.iframeRef,
  });
  // Устройство по умолчанию — по ширине колонки канвы (один раз за вход в конструктор).
  const { deviceChosen, layout } = b;
  const { setWidth } = state;
  const columnRef = frame.containerRef;
  useEffect(() => {
    if (deviceChosen.current) return;
    const px = columnRef.current?.clientWidth ?? 0;
    if (!px) return;
    deviceChosen.current = true;
    if (layout !== "desktop") setWidth(deviceForCanvas(px));
  }, [deviceChosen, layout, setWidth, columnRef]);

  // Клики по блокам и элементам, «+ блок сюда»; «Сейчас на сайте» и чужой источник — без рамок.
  // На телефоне шторка сжимает канву — блок прокручивается в видимую часть.
  const bridge = useCanvasBridge({ mode, refocusKey: toolbar });

  const src = useMemo(() => {
    if (mode === "live") return view.siteUrl ? liveFrameUrl(view.siteUrl, path) : null;
    if (!token.token) return null;
    if (pageVersion) return pageRevisionFrameUrl(token.token.previewUrl, pageVersion.pageId, pageVersion.version);
    // Без якоря блока: его смена — полная навигация через /site-preview; к блоку подводит block-highlight.
    return draftFrameUrl(token.token.previewUrl, path);
  }, [mode, view.siteUrl, path, token.token, pageVersion]);

  // Сохранено то, что витрина собирает на сервере, — перерисовать с задержкой; цвета и шрифты — живым сообщением.
  const versionTick = useCanvasReloadTick(canvasReloadKey(view.draft));

  // Пока сайт грузится — заглушка; «Повторить» перезагружает канву.
  const [retry, setRetry] = useState(0);
  const frameKey = `${pages.canvasVersion}:${versionTick}:${mode}:${override?.label ?? ""}:${retry}`;
  const loadKey = `${frameKey}|${src ?? ""}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  // Живой предпросмотр (SB120): новое окно канвы — пока не готово, до onLoad.
  useEffect(() => canvasPreviewUnloaded(), [loadKey]);
  // Корзина и баннер cookie поверх сайта — только на своём черновике.
  const overlays = useCanvasOverlays({ iframeRef: b.iframeRef, ownDraft: mode === "draft" && !override, selection, parts: view.parts, loadKey });
  const { cart } = overlays;
  const pageBg = view.parts?.theme.theme.colors.pageBg ?? "#ffffff";

  return (
    <div className={cn("flex min-h-0 min-w-0 flex-col gap-2", className)}>
      {toolbar ? (
        <CanvasToolbar
          width={state.width}
          onWidth={(w) => {
            deviceChosen.current = true;
            state.setWidth(w);
          }}
          mode={mode}
          onMode={override ? null : state.setMode}
          scale={scale}
          onScale={setScale}
          onFullscreen={b.openWholeSite}
        />
      ) : null}
      {override || !toolbar ? null : <CanvasTargetBar />}
      {overlays.cartActive ? <CanvasCartNotice failed={cart.failed} closed={cart.closed} onRetry={cart.retry} /> : null}
      {pageVersion && override ? (
        <CanvasVersionBar label={override.label} action={override.action} onClose={() => b.setOverride(null)} />
      ) : override ? (
        <NoticeStrip
          tone="info"
          action={
            <div className="flex flex-wrap gap-2">
              {override.action ? <Button onClick={override.action.run}>{override.action.label}</Button> : null}
              <Button variant="outline" onClick={() => b.setOverride(null)}>
                Закрыть
              </Button>
            </div>
          }
        >
          {override.label} — черновик не изменён.
        </NoticeStrip>
      ) : null}
      <div
        ref={frame.containerRef}
        // Непрозрачный фон цветом страницы: под коротким сайтом не просвечивает кабинет.
        style={{ background: pageBg }}
        className={cn("relative min-h-0 min-w-0 flex-1 rounded-xl", scale === "fit" ? "overflow-hidden" : "overflow-auto overscroll-contain")}
      >
        {mode === "draft" && token.error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="type-body max-w-[46ch] text-on-surface-variant">{UNAVAILABLE}</p>
            <Button variant="outline" onClick={token.retry}>
              <RefreshCw aria-hidden />
              Повторить
            </Button>
          </div>
        ) : src ? (
          <iframe
            key={frameKey}
            ref={b.iframeRef}
            src={src}
            title={mode === "draft" ? "Канва: черновик сайта" : "Сайт сейчас"}
            onLoad={() => {
              setLoadedKey(loadKey);
              frame.onLoad();
              bridge.onLoad();
              overlays.onLoad();
              canvasPreviewLoaded(mode === "draft" && !override);
            }}
            className="absolute left-1/2 top-0 origin-top border-0"
            style={{ ...frame.style, background: pageBg }}
          />
        ) : null}
        {src && loadedKey !== loadKey ? <CanvasLoading key={loadKey} onRetry={() => setRetry((n) => n + 1)} /> : null}
        {!src && !(mode === "draft" && token.error) ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6" aria-busy="true">
            <Skeleton className="h-8 w-2/3 rounded-lg" />
            <p className="type-caption text-on-surface-variant">
              {mode === "live" && !view.siteUrl ? "У магазина пока нет адреса сайта." : "Загружаем сайт…"}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
